import { ChangeEvent } from 'react';
import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CoreBox, CoreCheckbox, CoreFormControl, CoreFormControlLabel, CoreTypography } from '@youscience/core';
import {
  MidYearReportRequest,
  MidYearReportSchema,
  YesNo,
  TranscriptGradeInclusionMRFR,
} from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import SectionAccordion from '@components/SectionAccordion';
import ContinueButton from '@components/ContinueButton';

import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';

import { getFilename } from '../../../../common/formUtils';
import FileUpload from '../../../../common/FileUpload';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';
import { TabSections } from '@interfaces/recommenderWorkspace';

export type TranscriptsFields = Partial<MidYearReportRequest[TabSections.Transcripts]>;

interface TranscriptsProps {
  transcripts?: TranscriptsFields;
  onFormChange: (formState: FormState<TranscriptsFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = MidYearReportSchema.shape.transcripts;

export const Transcripts = ({
  transcripts = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
  applicantName = '',
  showError = false,
}: TranscriptsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<TranscriptsFields, typeof validator>(
    transcripts,
    validator,
    isSaved,
    onFormChange,
  );

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked ? YesNo.Yes : undefined });
  };

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    setFormStateWithErrors({ [field]: file });
  };

  return (
    <SectionAccordion
      title="Transcripts"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <FileUpload
          title="Upload current or most recent transcript"
          name="currentTranscript"
          initialFile={formState.data.currentTranscript}
          previewTitle="Current Transcript"
          filename={formState.data.currentTranscript ? getFilename(applicantName, 'current_transcript') : undefined}
          onFileUpload={handleFileChange}
          onFileDelete={handleFileChange}
          error={formState.errors.currentTranscript}
        />

        <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          <CoreTypography variant="subtitle2" component="h4">
            Transcript Affirmation
          </CoreTypography>

          <CoreFormControlLabel
            name="affirmation"
            label={
              <CoreTypography>
                I affirm that I have uploaded a transcript or other document listing coursework and available
                grades/marks to this Mid Year Report. I understand that failure to do so (by, for example, uploading a
                blank document or uploading a document promising to mail the transcript at a later time) will result in
                my Common App Online account being closed.
              </CoreTypography>
            }
            control={
              <CoreCheckbox
                value={true}
                onChange={handleCheckboxChange}
                checked={formState.data.affirmation === YesNo.Yes}
              />
            }
          />

          {formState.errors.affirmation && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
        </CoreBox>

        <CoreFormControl fullWidth error={!!formState.errors.grades}>
          <InputLabel>
            Please indicate the most recent grades included on the transcript accompanying this form. *
          </InputLabel>

          <Select
            label="Please indicate the most recent grades included on the transcript accompanying this form."
            value={formState.data.grades ?? ''}
            onChange={handleSelectChange('grades')}
          >
            {Object.entries(TranscriptGradeInclusionMRFR).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>
          {formState.errors.grades && <FormHelperText>{formState.errors.grades}</FormHelperText>}
        </CoreFormControl>
        <ContinueButton onContinue={onContinue} />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
